import { useState, FC, useEffect } from "react";
import { toastService } from "@services/ToastService";
import { userService } from "@services/UserServices";
import { ProjectOverview } from "@shared/interfaces/ProjectsFiles";
import { InvitedBy } from "@shared/interfaces/User";
import { projectService } from "@services/ProjectServices";
import { useParams } from "react-router-dom";
import Loader from "@shared/components/Loader";
import { useUser } from "@services/UserContext";

interface JoinProjectModalProps {
  onClose: () => void;
  invitedBy: InvitedBy;
}

const JoinProjectModal: FC<JoinProjectModalProps> = ({
  invitedBy,
  onClose,
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [project, setProject] = useState<ProjectOverview>(
    {} as ProjectOverview
  );

  const { refresh, setRefresh } = useUser();
  const [submitting, setSubmitting] = useState(false);

  const isSubmitDisabled = !firstName || !lastName;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (firstName && lastName) {
      try {
        setSubmitting(true);
        await userService.updateNames({
          first_name: firstName,
          last_name: lastName,
        });
        toastService.success("Welcome!");

        setRefresh(refresh + 1); // Trigger a refresh in UserContext
        setTimeout(() => {
          onClose();
          setLoading(false);
        }, 500);
      } catch (error) {
        console.error("Failed to update names:", error);
        toastService.error(
          "An unexpected error occurred. Please try again later."
        );
        setSubmitting(false);
      }
    }
  };

  const { projectId } = useParams();
  const [loading, setLoading] = useState(false);

  const fetchProject = async () => {
    try {
      setLoading(true);
      if (projectId) {
        const response = await projectService.getProjectById(projectId);
        if (response) {
          setProject(response);
        } else {
          toastService.error("Failed to fetch project details.");
        }
      }
    } catch (error) {
      console.error("Failed to fetch project:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProject();
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="relative w-full max-w-md p-6 bg-white rounded-lg shadow-lg">
        <div className="flex flex-col items-center text-center min-h-96">
          <img src="/logo_new.svg" alt="The Ark" className="w-36 mb-4" />
          <h2 className="text-sm font-semibold mb-1">Welcome to The Ark</h2>
          <p className="text-sm text-gray-600 mb-6">
            Before joining, tell us your name so teammates know who you are.
          </p>
          {loading ? (
            <div className="flex items-center justify-center w-full flex-1 pb-12">
              <Loader color="#184CD8" size="lg" />
            </div>
          ) : (
            <>
              <div className="text-sm mb-6 text-left w-full text-gray-700">
                {project.name && (
                  <p>
                    <span className="text-[#98A2B2]">Project:</span>{" "}
                    {project.name}
                  </p>
                )}
                <p>
                  <span className="text-[#98A2B2]">Invited by:</span>{" "}
                  {invitedBy
                    ? `${invitedBy.first_name} ${invitedBy.last_name}`
                    : ""}
                </p>
              </div>
              <form onSubmit={handleSubmit} className="w-full">
                <div className="mb-6">
                  <label className="block text-left text-sm font-medium text-gray-700">
                    First name <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Add first name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-left text-sm font-medium text-gray-700">
                    Last name <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Add last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>
                <button
                  type="submit"
                  className={`w-full py-2 text-white flex items-center justify-center space-x-2 ${
                    isSubmitDisabled || submitting
                      ? "bg-blue-400 hover:bg-blue-500 cursor-not-allowed"
                      : "bg-blue-700 hover:bg-blue-800"
                  }  rounded-md transition`}
                  disabled={isSubmitDisabled || submitting}
                >
                  <span>Join Project</span>
                  <span>{submitting && <Loader color="#fff" size="sm" />}</span>
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default JoinProjectModal;
